import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    OutlinedInput,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';

import {
    IconSearch,
    IconFilter,
    IconCalendar,
    IconEye,
    IconBuilding,
    IconMessage2,
    IconFlame,
    IconSnowflake,
    IconTemperature,
    IconBrain,
    IconPhone,
    IconMail,
    IconMapPin,
    IconX,
    IconChevronRight,
    IconStar,
    IconClock
} from '@tabler/icons';

import { motion } from 'framer-motion';
import api from '../../../../services/api';

const MotionCard = motion(Card);

const DEAL_TYPES = {
    quente: {
        label: 'Quente',
        color: '#ef4444',
        bg: 'linear-gradient(135deg,#ef4444,#f97316)',
        icon: IconFlame
    },
    morno: {
        label: 'Morno',
        color: '#f59e0b',
        bg: 'linear-gradient(135deg,#f59e0b,#facc15)',
        icon: IconTemperature
    },
    frio: {
        label: 'Frio',
        color: '#3b82f6',
        bg: 'linear-gradient(135deg,#3b82f6,#06b6d4)',
        icon: IconSnowflake
    }
};

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const CRMContacts = () => {
    const theme = useTheme();
    const history = useHistory();

    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [temperatureFilter, setTemperatureFilter] = useState('all');
    const [dateFilter, setDateFilter] = useState('');
    const [selectedContact, setSelectedContact] = useState(null);
    const [openDetail, setOpenDetail] = useState(false);
    const [appId, setAppId] = useState();
    const [pipelines, setPipelines] = useState([]);
    const [pipelineId, setPipelineId] = useState(null);
    const [status, setStatus] = useState({ type: '', message: '' });

    const pegar_app_padrao = async () => {
        try {
            const response = await api.get('v1/apps/default/');
            setAppId(response.data.app.id);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        if (!localStorage.getItem('access_token')) return;
        pegar_app_padrao();
    }, []);

    useEffect(() => {
        if (!appId) return;
        async function loadPipeline() {
            try {
                const response = await api.get(`v1/apps/${appId}/pipelines/`);
                if (!response.data || response.data.length === 0) {
                    setStatus({ type: 'error', message: 'ERRO! NÃO EXISTEM PIPELINES CADASTRADAS!' });
                    setLoading(false);
                    return;
                }
                setPipelines(response.data);
                setPipelineId(response.data[0].id);
            } catch (error) {
                console.error(error);
                setStatus({ type: 'error', message: 'Erro ao carregar pipeline.' });
                setLoading(false);
            }
        }
        loadPipeline();
    }, [appId]);

    useEffect(() => {
        if (!appId || !pipelineId) return;
        async function loadContacts() {
            setLoading(true);
            try {
                if (!appId) return;
                if (!pipelineId) return;
                const response = await api.get(`v1/apps/${appId}/pipelines/${pipelineId}/deals/`);
                setContacts(response.data || []);
            } catch (error) {
                console.error(error);
                setStatus({ type: 'error', message: 'Erro ao carregar contatos.' });
            } finally {
                setLoading(false);
            }
        }
        loadContacts();
    }, [appId, pipelineId]);

    const filteredContacts = useMemo(() => {
        return contacts.filter((contact) => {
            const matchesSearch =
                !search ||
                contact.conversation_detail?.displayName?.toLowerCase().includes(search.toLowerCase()) ||
                contact.user_detail?.first_name?.toLowerCase().includes(search.toLowerCase()) ||
                contact.organization_detail?.name?.toLowerCase().includes(search.toLowerCase()) ||
                contact.user_detail?.email?.toLowerCase().includes(search.toLowerCase());

            const matchesTemp = temperatureFilter === 'all' || contact.status === temperatureFilter;

            const matchesDate = !dateFilter || contact.created_at?.startsWith(dateFilter);

            return matchesSearch && matchesTemp && matchesDate;
        });
    }, [contacts, search, temperatureFilter, dateFilter]);

    const openContactDetail = (contact) => {
        setSelectedContact(contact);
        setOpenDetail(true);
    };

    return (
        <Box>
            {/* HERO */}
            <Box
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '30px',
                    p: { xs: 3, md: 5 },
                    mb: 4,
                    background:
                        theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg,#111827 0%,#0f172a 100%)'
                            : 'linear-gradient(135deg,#f8fafc 0%,#ffffff 100%)',
                    border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(15,23,42,0.06)',
                    boxShadow: theme.palette.mode === 'dark' ? '0 10px 40px rgba(0,0,0,0.35)' : '0 10px 40px rgba(15,23,42,0.06)'
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        width: 320,
                        height: 320,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(59,130,246,0.18), rgba(168,85,247,0.15))',
                        top: -120,
                        right: -80,
                        filter: 'blur(50px)'
                    }}
                />

                <Box sx={{ position: 'relative', zIndex: 2 }}>
                    <Chip icon={<IconStar size={14} />} label="CRM Inteligente" sx={{ mb: 2, fontWeight: 700 }} />

                    <Typography
                        variant="h1"
                        sx={{
                            fontWeight: 900,
                            fontSize: { xs: '2rem', md: '3rem' },
                            letterSpacing: '-2px',
                            mb: 1,
                            background: 'linear-gradient(90deg,#2563eb,#7c3aed)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}
                    >
                        Contatos do Funil
                    </Typography>

                    <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 700, lineHeight: 1.8, mb: 4 }}>
                        Gerencie leads quentes, mornos e frios, visualize empresas, histórico de conversa e resumos gerados por IA.
                    </Typography>

                    {/* FILTERS */}
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={5}>
                            <OutlinedInput
                                fullWidth
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Buscar contato..."
                                startAdornment={
                                    <InputAdornment position="start">
                                        <IconSearch size={18} />
                                    </InputAdornment>
                                }
                                sx={{
                                    height: 56,
                                    borderRadius: '18px',
                                    background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fff'
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                select
                                value={temperatureFilter}
                                onChange={(e) => setTemperatureFilter(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IconFilter size={18} />
                                        </InputAdornment>
                                    )
                                }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '18px', height: 56 } }}
                            >
                                <MenuItem value="all">Todos</MenuItem>
                                <MenuItem value="quente">Quente</MenuItem>
                                <MenuItem value="morno">Morno</MenuItem>
                                <MenuItem value="frio">Frio</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                type="date"
                                value={dateFilter}
                                onChange={(e) => setDateFilter(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IconCalendar size={18} />
                                        </InputAdornment>
                                    )
                                }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '18px', height: 56 } }}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </Box>

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 3, borderRadius: '16px' }} onClose={() => setStatus({ type: '', message: '' })}>
                    {status.message}
                </Alert>
            )}

            {/* CONTENT */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                    <CircularProgress />
                </Box>
            ) : filteredContacts.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 10, color: 'text.secondary' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, opacity: 0.5 }}>
                        Nenhum contato encontrado
                    </Typography>
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {filteredContacts.map((contact, index) => {
                        const deal = DEAL_TYPES[contact.status] || DEAL_TYPES.morno;
                        const DealIcon = deal.icon;

                        return (
                            <Grid item xs={12} md={6} lg={4} key={contact.id}>
                                <MotionCard
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.03 }}
                                    whileHover={{ y: -5 }}
                                    sx={{
                                        borderRadius: '24px',
                                        overflow: 'hidden',
                                        border:
                                            theme.palette.mode === 'dark'
                                                ? '1px solid rgba(255,255,255,0.06)'
                                                : '1px solid rgba(15,23,42,0.06)',
                                        background:
                                            theme.palette.mode === 'dark' ? 'linear-gradient(180deg,#111827 0%,#0f172a 100%)' : '#fff',
                                        boxShadow:
                                            theme.palette.mode === 'dark'
                                                ? '0 10px 30px rgba(0,0,0,0.35)'
                                                : '0 10px 30px rgba(15,23,42,0.06)'
                                    }}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar src={contact.conversation_detail?.avatarUrl} sx={{ width: 58, height: 58 }}>
                                                    {contact.conversation_detail?.displayName?.charAt(0) ||
                                                        contact.user_detail?.first_name?.charAt(0)}
                                                </Avatar>

                                                <Box>
                                                    <Typography variant="h5" sx={{ fontWeight: 800 }}>
                                                        {contact.conversation_detail?.displayName ||
                                                            `${contact.user_detail?.first_name || ''} ${contact.user_detail?.last_name || ''}`.trim() ||
                                                            contact.title}
                                                    </Typography>

                                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                        {contact.organization_detail?.name || '—'}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            <Chip
                                                icon={<DealIcon size={14} />}
                                                label={deal.label}
                                                sx={{ background: deal.bg, color: '#fff', fontWeight: 700 }}
                                            />
                                        </Box>

                                        <Divider sx={{ my: 2 }} />

                                        <Stack spacing={1.2}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <IconMail size={16} />
                                                <Typography variant="body2">
                                                    {contact.user_detail?.email || contact.organization_detail?.email || '—'}
                                                </Typography>
                                            </Box>

                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <IconPhone size={16} />
                                                <Typography variant="body2">{contact.user_detail?.phone || '—'}</Typography>
                                            </Box>

                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <IconClock size={16} />
                                                <Typography variant="body2">Última interação: {formatDate(contact.updated_at)}</Typography>
                                            </Box>
                                        </Stack>

                                        <Divider sx={{ my: 2 }} />

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                                            <Tooltip title="Ver detalhes">
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<IconEye size={16} />}
                                                    sx={{ borderRadius: '14px', textTransform: 'none' }}
                                                    onClick={() => openContactDetail(contact)}
                                                >
                                                    Detalhes
                                                </Button>
                                            </Tooltip>

                                            <Tooltip title="Ver empresa">
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<IconBuilding size={16} />}
                                                    sx={{ borderRadius: '14px', textTransform: 'none' }}
                                                    onClick={() => openContactDetail(contact)}
                                                >
                                                    Empresa
                                                </Button>
                                            </Tooltip>

                                            <Tooltip title="Resumo do negócio">
                                                <Button
                                                    variant="contained"
                                                    startIcon={<IconBrain size={16} />}
                                                    sx={{
                                                        borderRadius: '14px',
                                                        textTransform: 'none',
                                                        background: 'linear-gradient(135deg,#7c3aed,#2563eb)'
                                                    }}
                                                    onClick={() => openContactDetail(contact)}
                                                >
                                                    IA
                                                </Button>
                                            </Tooltip>
                                        </Box>

                                        <CardActionArea onClick={() => openContactDetail(contact)} sx={{ mt: 2, borderRadius: '16px' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    Abrir detalhes
                                                </Typography>
                                                <IconChevronRight size={18} />
                                            </Box>
                                        </CardActionArea>
                                    </CardContent>
                                </MotionCard>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {/* DETAIL MODAL */}
            <Dialog
                open={openDetail}
                onClose={() => setOpenDetail(false)}
                fullWidth
                maxWidth="md"
                PaperProps={{ sx: { borderRadius: '26px' } }}
            >
                {selectedContact && (
                    <>
                        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar src={selectedContact.conversation_detail?.avatarUrl} sx={{ width: 58, height: 58 }}>
                                    {selectedContact.conversation_detail?.displayName?.charAt(0) ||
                                        selectedContact.user_detail?.first_name?.charAt(0)}
                                </Avatar>

                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 800 }}>
                                        {selectedContact.conversation_detail?.displayName ||
                                            `${selectedContact.user_detail?.first_name || ''} ${selectedContact.user_detail?.last_name || ''}`.trim() ||
                                            selectedContact.title}
                                    </Typography>

                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        {selectedContact.organization_detail?.name || '—'}
                                    </Typography>
                                </Box>
                            </Box>

                            <IconButton onClick={() => setOpenDetail(false)}>
                                <IconX size={18} />
                            </IconButton>
                        </DialogTitle>

                        <DialogContent>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Card sx={{ borderRadius: '20px' }}>
                                        <CardContent>
                                            <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                                                Informações
                                            </Typography>

                                            <Stack spacing={1.5}>
                                                <Typography>
                                                    <strong>Nome:</strong>{' '}
                                                    {`${selectedContact.user_detail?.first_name || ''} ${selectedContact.user_detail?.last_name || ''}`.trim() ||
                                                        '—'}
                                                </Typography>

                                                <Typography>
                                                    <strong>Email:</strong>{' '}
                                                    {selectedContact.user_detail?.email ||
                                                        selectedContact.organization_detail?.email ||
                                                        '—'}
                                                </Typography>

                                                <Typography>
                                                    <strong>Telefone:</strong> {selectedContact.user_detail?.phone || '—'}
                                                </Typography>

                                                <Typography>
                                                    <strong>Empresa:</strong> {selectedContact.organization_detail?.name || '—'}
                                                </Typography>

                                                {selectedContact.organization_detail?.cnpj && (
                                                    <Typography>
                                                        <strong>CNPJ:</strong> {selectedContact.organization_detail.cnpj}
                                                    </Typography>
                                                )}

                                                {selectedContact.organization_detail?.address && (
                                                    <Typography>
                                                        <strong>Endereço:</strong> {selectedContact.organization_detail.address}
                                                    </Typography>
                                                )}

                                                <Typography>
                                                    <strong>Estágio:</strong> {selectedContact.stage_name || '—'}
                                                </Typography>

                                                <Typography>
                                                    <strong>Última atualização:</strong> {formatDate(selectedContact.updated_at)}
                                                </Typography>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Card
                                        sx={{
                                            borderRadius: '20px',
                                            background: 'linear-gradient(135deg,#7c3aed,#2563eb)',
                                            color: '#fff'
                                        }}
                                    >
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                                <IconBrain />
                                                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                                                    Resumo do Negócio
                                                </Typography>
                                            </Box>

                                            <Stack spacing={1}>
                                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                                    <strong>Título:</strong> {selectedContact.title || '—'}
                                                </Typography>
                                                {selectedContact.description && (
                                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                                        <strong>Descrição:</strong> {selectedContact.description}
                                                    </Typography>
                                                )}
                                                {selectedContact.value && parseFloat(selectedContact.value) > 0 && (
                                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                                        <strong>Valor:</strong>{' '}
                                                        {parseFloat(selectedContact.value).toLocaleString('pt-BR', {
                                                            style: 'currency',
                                                            currency: 'BRL'
                                                        })}
                                                    </Typography>
                                                )}
                                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                                    <strong>Pipeline:</strong> {selectedContact.pipeline_name || '—'}
                                                </Typography>
                                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                                    <strong>Estágio:</strong> {selectedContact.stage_name || '—'}
                                                </Typography>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12}>
                                    <Card sx={{ borderRadius: '20px' }}>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                                <IconMessage2 />
                                                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                                                    Última conversa
                                                </Typography>
                                            </Box>

                                            <Typography
                                                variant="body2"
                                                sx={{ lineHeight: 1.9, color: 'text.secondary', whiteSpace: 'pre-wrap' }}
                                            >
                                                {selectedContact.conversation_detail?.last_message || 'Nenhuma mensagem registrada.'}
                                            </Typography>

                                            {selectedContact.conversation_detail?.last_message_time && (
                                                <Typography variant="caption" sx={{ color: 'text.disabled', mt: 1, display: 'block' }}>
                                                    {formatDate(selectedContact.conversation_detail.last_message_time)}
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </DialogContent>

                        <DialogActions sx={{ px: 3, pb: 3 }}>
                            <Button
                                variant="contained"
                                startIcon={<IconMessage2 size={18} />}
                                sx={{
                                    borderRadius: '14px',
                                    px: 3,
                                    textTransform: 'none',
                                    background: 'linear-gradient(135deg,#2563eb,#7c3aed)'
                                }}
                                onClick={() => {
                                    if (selectedContact.conversation_detail?.id) {
                                        history.push(`/dashboard/chats?id=${selectedContact.conversation_detail.id}`);
                                    }
                                }}
                            >
                                Abrir Conversa
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
};

export default CRMContacts;
